"use client";

import LocalizationServiceability from "@/components/LocalizationServiceability";
import { useAppContext } from "@/context/AppContext";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import cn from "@/lib/cn";
import type {
  NavBarLocalizationCtaMobileDisplay,
  NavBarLocalizationProps,
} from "./types";

const DEFAULT_PLACEHOLDER = "Enter your address";
const DEFAULT_CTA_COPY = "Shop plans";
const SUBMITTING_CTA_COPY = "Checking...";
const INPUT_ID = "nav-bar-localization-address-input";

type NavBarLocalizationComponentProps = NavBarLocalizationProps & {
  className?: string;
};

export default function NavBarLocalization({
  elementName,
  label,
  input,
  ctas,
  tagularProps,
  className,
}: NavBarLocalizationComponentProps) {
  const { isLoading, localizationType } = useAppContext();
  const contextMonarchMetadata = useMonarchMetadata();
  const monarchMetadata =
    tagularProps?.monarchMetadata ?? contextMonarchMetadata;

  const trackingName = elementName?.trim() || "NavBarLocalization";
  const primaryCta = ctas?.[0];
  const mobileDisplay: NavBarLocalizationCtaMobileDisplay =
    primaryCta?.mobileDisplay === "icon" ? "icon" : "text";
  const primaryCtaCopy = primaryCta?.copy?.trim() || DEFAULT_CTA_COPY;
  const placeholderCopy =
    input?.placeholder?.copy?.trim() || DEFAULT_PLACEHOLDER;
  const labelCopy = label?.copy?.trim() || "";
  const location = tagularProps?.location ?? "header";
  const actionOutcome = tagularProps?.actionOutcome ?? "sentToLocalize";

  const labelVisibility = cn(
    label?.show?.mobile === false ? "hidden" : "block",
    label?.show?.tablet === false ? "md:hidden" : "md:block",
    label?.show?.desktop === false ? "lg:hidden" : "lg:block"
  );

  if (isLoading || localizationType === "full") {
    return null;
  }

  return (
    <LocalizationServiceability
      showLocationIcon={false}
      showSelectedAddress={false}
      ctaPlacement="inline"
      mobileCtaDisplay={mobileDisplay}
      suggestionsTestId="nav-bar-localization-suggestions"
      field={{
        inputId: INPUT_ID,
        label: labelCopy,
        labelClassName: cn(
          "body-xs-normal mb-1 text-neutral-base",
          labelVisibility
        ),
        placeholder: placeholderCopy,
        // The label can be hidden per breakpoint, so name the input directly
        ariaLabel: labelCopy || placeholderCopy,
        autoComplete: "street-address",
      }}
      primaryCta={{
        copy: primaryCtaCopy,
        loadingCopy: SUBMITTING_CTA_COPY,
        className: "w-auto shrink-0 md:min-w-40",
      }}
      inputContainerClassName={cn("w-full", className)}
      rowClassName="flex-row items-end gap-2"
      inputWrapperClassName="md:w-full md:max-w-96.25"
      inputClassName="body-base-normal h-14 rounded border-(--bb-label-ui-text-neutral2-base) text-neutral-base placeholder:text-neutral-600 focus:border-neutral-950"
      ctaContainerClassName="shrink-0 self-end"
      statusWrapperClassName="empty:hidden"
      inputEngagedTracking={{
        elementType: "textInput",
        location,
        text: "Address input focused",
        name: `textInput${trackingName}Address`,
        ...(monarchMetadata && { monarchMetadata }),
      }}
      submitTracking={{
        elementType: "ctaButton",
        location,
        name: `ctaButton${trackingName}Primary`,
        actionOutcomeWhenLocalized: actionOutcome,
        actionOutcomeWhenNotLocalized: actionOutcome,
        ...(monarchMetadata && { monarchMetadata }),
      }}
    />
  );
}
